import React, { useEffect } from 'react';
import { View, TouchableOpacity } from 'react-native';
import csv from 'csvtojson';
import { Table, TableWrapper, Cell } from 'react-native-table-component';
import { Box, Link, Text, HStack, useColorModeValue } from 'native-base';
import { textStyle } from 'src/styles/textStyle';
import { tableStyle } from 'src/styles/tableStyle';
import { useSelector } from 'react-redux';
import { IStore } from 'src/redux/reducers';
import { isElectron } from 'src/utils/miscUtils';
import { getText } from 'src/utils/i18n';
import { localizeResultCell, localizeResultLabel } from 'src/utils/resultLocalization';

interface DisplayCsvTableComponentProps {
  title?: string;
  columns?: number[];
  csvFileUrl?: string;
  tableHead?: any[];
  tableData?: any[][];
  showHead?: boolean;
}

export default function DisplayCsvTableComponent(props: DisplayCsvTableComponentProps) {

  const colorMode = useSelector((store: IStore) => store.settings.colorMode);
  const locale = useSelector((store: IStore) => store.app.locale);
  const tableHeadBg = useColorModeValue('#f1f8ff', '#2d3748');
  const tableBorderColor = useColorModeValue('#c8e1ff', '#4a5568');
  const titleTextColor = useColorModeValue('coolGray.800', 'coolGray.100');

  const [state, setState] = React.useState<{
    title: string;
    columns: number[];
    tableHead: any[];
    showHead: boolean;
    tableData: any[][];
  }>({
    title: "",
    columns: [],
    tableHead: [],
    showHead: true,
    tableData: []
  });

  const [sortByColumn, setSortByColumn] = React.useState<string | null>(null);
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (props.csvFileUrl != null && props.csvFileUrl != undefined && props.csvFileUrl != "") {
      setTableData(props.title || "", props.columns || [], props.csvFileUrl);
    } else {
      setTableDataUsingProps()
    }
  }, [props.csvFileUrl, props.title, props.columns]);

  useEffect(() => {
    setTableDataUsingProps()
  }, [props.tableData]);

  const readCsvText = async (csvFileUrl: string) => {
    if (csvFileUrl.startsWith('file://')) {
      if (!isElectron() || !(window as any).electron?.readLocalFile) {
        throw new Error('Local file access not available');
      }
      return (window as any).electron.readLocalFile(csvFileUrl);
    }

    const response = await fetch(csvFileUrl);
    return response.text();
  };

  const setTableData = async (title: string, cols: any, csvFileUrl: string) => {
    try {
      const resp = await readCsvText(csvFileUrl);
      const csvRow = await csv({
        noheader: true,
        output: "csv"
      }).fromString(resp);

      setState((prevState) => ({
        ...prevState,
        title: title,
        columns: cols || Array.from(Array(csvRow[0].length).keys()),
        tableHead: csvRow[0],
        tableData: csvRow.slice(1)
      }));
    } catch (error) {
      console.error("some error occurred", error);
    }
  }

  const setTableDataUsingProps = () => {
    setState((prevState) => ({
      ...prevState,
      title: props.title || "",
      columns: props.columns || [],
      tableHead: props.tableHead || [],
      tableData: props.tableData || [],
    }));
  }

  const beautify = (data: any) => {
    if (!isNaN(parseFloat(data)) && !isNaN(data - 0)) {
      let floatData = parseFloat(data);
      if (Math.ceil(floatData) == Math.floor(floatData)) {
        return data;
      } else {
        return floatData.toFixed(2);
      }
    } else {
      return data;
    }
  }

  const sortItems = (column: string) => {
    if (sortByColumn === column) {
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      applySorting(column, newSortOrder);
    } else {
      setSortByColumn(column);
      setSortOrder("asc");
      applySorting(column, 'asc');
    }
  }

  const applySorting = (column: string, order: "asc" | "desc") => {
    const columnIndex = state.tableHead.indexOf(column);
    if (columnIndex === -1) return;

    const sortedData = [...state.tableData].sort((a: any, b: any) => {
      const aVal = a[columnIndex];
      const bVal = b[columnIndex];

      // Handle boolean-like strings
      if (aVal === 'True' || aVal === 'False' || bVal === 'True' || bVal === 'False') {
        return order === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle numeric values
      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return order === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Handle string values
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    setState((prevState) => ({
      ...prevState,
      tableData: sortedData
    }));
  }

  const textElement = (data: any) => (
    <Text style={[tableStyle.cellText, {
      color: colorMode === 'dark' ? 'white' : 'black'
    }]}>{localizeResultCell(data, { locale })}</Text>
  );

  const linkElement = (data: any) => (
    <Link
      isExternal
      style={tableStyle.cellText}
      href={data}
    >{getText('clickToView')}</Link>
  );

  const headElement = (data: any) => (
    <TouchableOpacity onPress={() => sortItems(data)}>
      <HStack space={1} alignItems="center">
        <Text style={[tableStyle.cellText, {
          color: colorMode === 'dark' ? 'white' : 'black'
        }]}>{localizeResultLabel(data, { locale })}</Text>
        {sortByColumn === data && (
          <Text style={[tableStyle.cellText, {
            color: colorMode === 'dark' ? 'white' : 'black'
          }]}>{sortOrder === 'asc' ? '↑' : '↓'}</Text>
        )}
      </HStack>
    </TouchableOpacity>
  );

  const dataElement = (data: any) => {
    if (React.isValidElement(data)) {
      // Allow rendering richer cell content (e.g. buttons/links/layout) without
      // forcing everything into a <Text> wrapper which breaks spacing on web.
      return <Box style={tableStyle.cellText}>{data}</Box>;
    }

    return textElement(beautify(data));
  }

  return (
    <Box>
      <Text color={titleTextColor} style={textStyle.tableTitleText}>{state.title}</Text>
      <Table borderStyle={{ borderWidth: 2, borderColor: tableBorderColor }}>
        <TableWrapper style={[tableStyle.head, { backgroundColor: tableHeadBg }]}>
          {
            state.tableHead?.filter((cellData, index) => {
              return state.columns.includes(index);
            }).map((cellData, index) => (
              <Cell key={index} data={headElement(cellData)} />
            )) as any
          }
        </TableWrapper>
        {
          (state.tableData || []).map((rowData, index) => {
            return (
              <TableWrapper key={index} style={tableStyle.row}>
                {
                  rowData?.filter((rowData: any, index: number) => {
                    return state.columns.includes(index);
                  }).map((cellData: any, cellIndex: number) => (
                    <Cell key={cellIndex} data={dataElement(beautify(cellData))} />
                  )) as any
                }
              </TableWrapper>
            )
          }) as any
        }
      </Table>
    </Box>
  );
};
